import { LogoutAction } from '../User/User.types'

export enum ComprehensionType {
  TEXT = 'Text',
  AUDIO = 'Audio',
  IMAGE = 'Image'
}

export type ConversationElement = {
  id: string
  videoUrl: string
  sentence?: string
  videoScript?: string
  gapIndices: number[]

  isSummary?: boolean // added on the client side
}

export type ReadSentenceExerciseData = { sentence: string; sentenceUs?: string }

export type ConversationExerciseData = {
  conversationElements: ConversationElement[]
}

export type WordRaceExerciseData = { words: string[] }
export type WordPracticeExerciseData = { words: string[] }
export type BuildingExerciseData = {
  wordOrSentence: string
  missingIndexes: number[]
  wordOrSentenceUs?: string
  missingIndexesUs?: number[]
  distractors?: string[]
  distractorsUs?: string[]
}
export type ComprehensionExerciseData = {
  answer: number
  options: string[]
  imageOption1?: string
  imageOption2?: string
  imageOption3?: string
  imageOption4?: string
  audioOption1?: string
  audioOption2?: string
  audioOption3?: string
  audioOption4?: string
  audioOptionUs1?: string
  audioOptionUs2?: string
  audioOptionUs3?: string
  audioOptionUs4?: string
  question: string
  audioQuestion?: string
  audioQuestionUs?: string
  imageQuestion?: string
  answerUs?: number
  optionsUs?: string[]
  questionUs?: string
  answerType: ComprehensionType
  questionType: ComprehensionType
}

export interface SimpleWordScore {
  word: string
  score: number
}

export interface StudySet {
  _id: string
  name: string
  words: SimpleWordScore[]
}

export enum ExerciseType {
  ReadWordRace = 'ReadWordRace',
  ReadSentence = 'ReadSentence',
  WordBuilder = 'WordBuilder',
  SentenceBuilder = 'SentenceBuilder',
  MultiChoiseQuestion = 'MultiChoiseQuestion',
  WordPractice = 'WordPractice',
  Conversation = 'Conversation'
}

export type ExerciseData =
  | ReadSentenceExerciseData
  | WordRaceExerciseData
  | BuildingExerciseData
  | ComprehensionExerciseData
  | WordPracticeExerciseData
  | ConversationExerciseData

export type Exercise = {
  _id: string
  data: ExerciseData
  imageUrl?: string
  recordingUrl?: string
  points: number
  type: ExerciseType
  bookId: string
}

export enum ExerciseServiceActionTypes {
  AsyncGetAllByBookId = 'Exercise/async_get_all_by_book_id',
  AsyncGetMultipleByType = 'Exercise/async_get_multiple_by_type',
  AsyncGetDifficultWordsAndMasteredCount = 'Exercise/async_get_difficult_words_and_mastered_count',
  AsyncGetStudySets = 'Exercise/async_get_study_set',
  AsyncCreateNewStudySet = 'Exercise/async_create_new_study_set',
  SetCurrentStudySetId = 'Exercise/set_current_study_set_id',
  AsyncUpdateSingleStudySet = 'Exercise/async_update_single_study_set',
  AsyncDeleteStudySet = 'Exercise/async_delete_study_set'
}

export type AsyncGetAllByBookId = {
  type: ExerciseServiceActionTypes.AsyncGetAllByBookId
  exerciseEntities: Exercise[]
  bookId: string
}

export type AsyncGetStudySets = {
  type: ExerciseServiceActionTypes.AsyncGetStudySets
  studySets: StudySet[]
}

export type AsyncCreateNewStudySet = {
  type: ExerciseServiceActionTypes.AsyncCreateNewStudySet
  studySet: StudySet
}

export type AsyncUpdateSingleStudySet = {
  type: ExerciseServiceActionTypes.AsyncUpdateSingleStudySet
  studySet: StudySet
}

export type SetCurrentStudySetId = {
  type: ExerciseServiceActionTypes.SetCurrentStudySetId
  setId: string | null
}

export type DeleteStudySet = {
  type: ExerciseServiceActionTypes.AsyncDeleteStudySet
  setId: string
}

export type AsyncGetDifficultWordsAndMasteredCount = {
  type: ExerciseServiceActionTypes.AsyncGetDifficultWordsAndMasteredCount
  difficultWords: SimpleWordScore[]
  masteredCount: number
}

export type AsyncGetMultipleByType = {
  type: ExerciseServiceActionTypes.AsyncGetMultipleByType
  exerciseType: ExerciseType
  exerciseEntities: Exercise[]
}

export type ExerciseServiceActions =
  | LogoutAction
  | AsyncGetAllByBookId
  | AsyncGetMultipleByType
  | AsyncGetDifficultWordsAndMasteredCount
  | AsyncGetStudySets
  | AsyncCreateNewStudySet
  | SetCurrentStudySetId
  | AsyncUpdateSingleStudySet
  | DeleteStudySet

export type ExerciseServiceInitialState = {
  entities: Exercise[]
  wordBuilderEntities: Exercise[]
  sentenceBuilderEntities: Exercise[]
  loadedExercisesBookIds: string[]
  difficultWords: SimpleWordScore[]
  masteredWordsCount: number
  studySets: StudySet[]
  currentStudySetId: string | null
}

export type DifficultWordsResponse = { difficultWords: SimpleWordScore[]; masteredCount: number }
